// src/pages/ArtworkGuidelinesPage.jsx
import { Link } from "react-router-dom";
import "./ArtworkGuidelinesPage.css";

const CheckIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 6 9 17l-5-5"/>
  </svg>
);
const XIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
  </svg>
);
const FileIcon = () => (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/>
  </svg>
);
const ImageIcon = () => (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/>
  </svg>
);
const PaletteIcon = () => (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="13.5" cy="6.5" r=".5" fill="currentColor"/><circle cx="17.5" cy="10.5" r=".5" fill="currentColor"/>
    <circle cx="8.5" cy="7.5" r=".5" fill="currentColor"/><circle cx="6.5" cy="12.5" r=".5" fill="currentColor"/>
    <path d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10c.926 0 1.648-.746 1.648-1.688 0-.437-.18-.835-.437-1.125-.29-.289-.438-.652-.438-1.125a1.64 1.64 0 0 1 1.668-1.668h1.996c3.051 0 5.555-2.503 5.555-5.554C21.965 6.012 17.461 2 12 2z"/>
  </svg>
);
const TypeIcon = () => (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="4 7 4 4 20 4 20 7"/><line x1="9" y1="20" x2="15" y2="20"/><line x1="12" y1="4" x2="12" y2="20"/>
  </svg>
);
const RulerIcon = () => (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21.3 8.7 8.7 21.3c-1 1-2.5 1-3.4 0l-2.6-2.6c-1-1-1-2.5 0-3.4L15.3 2.7c1-1 2.5-1 3.4 0l2.6 2.6c1 1 1 2.5 0 3.4Z"/>
    <path d="m7.5 10.5 2 2"/><path d="m10.5 7.5 2 2"/><path d="m13.5 4.5 2 2"/><path d="m4.5 13.5 2 2"/>
  </svg>
);

const SECTIONS = [
  {
    Icon: FileIcon,
    color: "cyan",
    title: "Accepted File Formats",
    content: (
      <div>
        <div className="ag-formats ag-formats-2col">
          <div className="ag-format-good">
            <div className="ag-format-head"><span className="ag-badge good"><CheckIcon /> Accepted</span></div>
            <div className="ag-format-list">
              <div className="ag-format-item"><strong>PDF</strong><span>Print-ready PDF is the only format we accept — it preserves fonts, colours, and layout exactly as designed</span></div>
            </div>
          </div>
          <div className="ag-format-bad">
            <div className="ag-format-head"><span className="ag-badge bad"><XIcon /> Not Accepted</span></div>
            <div className="ag-format-list">
              <div className="ag-format-item"><strong>AI / EPS / PSD / TIFF</strong><span>Please export or save your file as a PDF before submitting</span></div>
              <div className="ag-format-item"><strong>JPEG / PNG</strong><span>Not suitable for professional print — convert to PDF first</span></div>
              <div className="ag-format-item"><strong>Word / Publisher</strong><span>Not suitable for professional print</span></div>
            </div>
          </div>
        </div>
        <p className="ag-tip">💡 Tip: Before exporting your PDF, make sure all fonts are embedded, colours are set to CMYK, and bleed/safe zone are included.</p>
      </div>
    )
  },
  {
    Icon: ImageIcon,
    color: "magenta",
    title: "Resolution Requirements",
    content: (
      <div>
        <div className="ag-resolution-cards">
          <div className="ag-res-card good">
            <strong>300 DPI</strong>
            <span>Minimum for print</span>
            <p>Sharp, professional results</p>
          </div>
          <div className="ag-res-card ok">
            <strong>150 DPI</strong>
            <span>Large format only</span>
            <p>Banners &amp; posters viewed from distance</p>
          </div>
          <div className="ag-res-card bad">
            <strong>72 DPI</strong>
            <span>Screen only — NOT for print</span>
            <p>Will appear blurry when printed</p>
          </div>
        </div>
        <p className="ag-tip">💡 Tip: Always create your artwork at the actual print size. Scaling up a low-resolution file does not increase quality.</p>
      </div>
    )
  },
  {
    Icon: RulerIcon,
    color: "yellow",
    title: "Bleed, Trim & Safe Zone",
    content: (
      <div className="ag-bleed-section">
        <div className="ag-bleed-diagram">
          <div className="bleed-outer">
            <span className="bleed-label">Bleed Area (3mm)</span>
            <div className="bleed-trim">
              <span className="trim-label">Trim Line</span>
              <div className="bleed-safe">
                <span className="safe-label">Safe Zone (5mm from trim)</span>
                <div className="bleed-content">Your Content Here</div>
              </div>
            </div>
          </div>
        </div>
        <div className="ag-bleed-rules">
          <div className="ag-rule"><span className="ag-rule-icon cyan"><CheckIcon /></span><div><strong>3mm Bleed</strong><p>Extend background colours and images 3mm beyond the trim line on all sides</p></div></div>
          <div className="ag-rule"><span className="ag-rule-icon magenta"><CheckIcon /></span><div><strong>5mm Safe Zone</strong><p>Keep all text, logos and important content at least 5mm inside the trim line</p></div></div>
          <div className="ag-rule"><span className="ag-rule-icon red"><XIcon /></span><div><strong>Avoid placing text at edges</strong><p>Content too close to the edge risks being cut off during trimming</p></div></div>
        </div>
      </div>
    )
  },
  {
    Icon: PaletteIcon,
    color: "cyan",
    title: "Colour Mode",
    content: (
      <div className="ag-colour-section">
        <div className="ag-colour-cards">
          <div className="ag-colour-card good">
            <div className="cmyk-dots-ag">
              <span style={{background:"#00b4d8"}}>C</span>
              <span style={{background:"#e91e8c"}}>M</span>
              <span style={{background:"#f5c200", color:"#333"}}>Y</span>
              <span style={{background:"#1a1a2e"}}>K</span>
            </div>
            <strong>CMYK</strong>
            <span className="ag-badge good"><CheckIcon /> Use this for print</span>
            <p>Cyan, Magenta, Yellow, Key (Black). Standard for all professional printing.</p>
          </div>
          <div className="ag-colour-card bad">
            <div className="rgb-dots-ag">
              <span style={{background:"#ff0000"}}>R</span>
              <span style={{background:"#00cc00"}}>G</span>
              <span style={{background:"#0000ff"}}>B</span>
            </div>
            <strong>RGB</strong>
            <span className="ag-badge bad"><XIcon /> For screens only</span>
            <p>RGB colours appear vibrant on screen but will shift when converted to CMYK for printing.</p>
          </div>
        </div>
        <p className="ag-tip">💡 Tip: If your brand uses Pantone colours, request Pantone matching when ordering for colour-critical work.</p>
      </div>
    )
  },
  {
    Icon: TypeIcon,
    color: "magenta",
    title: "Fonts & Text",
    content: (
      <div className="ag-rules-list">
        <div className="ag-rule"><span className="ag-rule-icon cyan"><CheckIcon /></span><div><strong>Embed or outline all fonts</strong><p>When saving as PDF, embed all fonts. In Illustrator/Photoshop, convert text to outlines to avoid font substitution issues.</p></div></div>
        <div className="ag-rule"><span className="ag-rule-icon cyan"><CheckIcon /></span><div><strong>Minimum font size: 6pt</strong><p>Anything smaller may not be legible when printed. For reversed (white on dark) text, use a minimum of 8pt.</p></div></div>
        <div className="ag-rule"><span className="ag-rule-icon red"><XIcon /></span><div><strong>Avoid very thin fonts in small sizes</strong><p>Thin or light weight fonts below 8pt can fill in or break up during the print process.</p></div></div>
        <div className="ag-rule"><span className="ag-rule-icon red"><XIcon /></span><div><strong>Do not use RGB black for text</strong><p>Use pure CMYK black (C:0 M:0 Y:0 K:100) for body text. Rich black (C:60 M:40 Y:40 K:100) is suitable for large headings only.</p></div></div>
      </div>
    )
  },
];

export default function ArtworkGuidelinesPage() {
  return (
    <div className="ag-page">
      {/* Hero */}
      <div className="ag-hero anim-fade-in-up">
        <div className="container">
          <span className="tag">Artwork Help</span>
          <h1>Artwork Guidelines</h1>
          <p>Follow these guidelines to ensure your print files produce the best possible results.</p>
        </div>
      </div>

      {/* Quick Checklist */}
      <div className="ag-checklist-bar">
        <div className="container ag-checklist-inner">
          <strong>Quick Checklist:</strong>
          {["PDF format only", "300 DPI minimum", "3mm bleed added", "CMYK colour mode", "Fonts embedded/outlined", "Safe zone respected"].map(function(item) {
            return (
              <span key={item} className="ag-check-item"><CheckIcon /> {item}</span>
            );
          })}
        </div>
      </div>

      {/* Sections */}
      <div className="container ag-content">
        {SECTIONS.map(function(section) {
          return (
            <div key={section.title} className="ag-section">
              <div className="ag-section-header">
                <div className={"ag-section-icon " + section.color}><section.Icon /></div>
                <h2>{section.title}</h2>
              </div>
              <div className="ag-section-body">{section.content}</div>
            </div>
          );
        })}
      </div>

      {/* Bottom CTA */}
      <div className="ag-bottom-cta">
        <div className="container ag-cta-inner">
          <h3>Need Help With Your Artwork?</h3>
          <p>Our design team can prepare or fix your files for a small fee. Contact us and we'll get you sorted.</p>
          <div className="ag-cta-btns">
            <Link to="/contact" className="btn-primary">Get Artwork Help</Link>
            <Link to="/faq" className="btn-outline">View FAQs</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
